import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Eye, EyeOff, Plus, X, Save, Settings2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";

const Ctx = createContext(null);
export const PaidKeyCtx = createContext(null);
const EMPTY = { titles: {}, hidden: [], options: {}, extra: [], icons: {} };
const ICON_CHOICES = ["🚗", "🧽", "💧", "🧼", "✂️", "💈", "💇", "💅", "💄", "🧴", "🪒", "🛠️", "🔧", "🔨", "🪛", "⚙️", "🔌", "💡", "🧹", "🧺", "🚚", "📦", "🛵", "🅿️", "📶", "❄️", "🔥", "🏊", "🧖", "🍽️", "☕", "🍕", "🍺", "🎁", "🎉", "🎵", "📷", "💻", "📱", "🩺", "💊", "🦷", "🐾", "👶", "♿", "🚻", "🚭", "⚡", "🕒", "📅", "💳", "💶", "⭐", "✅", "🏠", "🛏️", "🌿", "🧘", "🏋️", "🎓"];
const NOOP = { edit: false, hidden: () => false, hiddenPaid: () => false, title: (t) => t, opts: (n, d) => d, cfg: EMPTY };
let allP = null;

export function FormCfgProvider({ cat, initial, children }) {
  const { user } = useAuth();
  const admin = can(user, "settings");
  const [all, setAll] = useState({});
  const [sub, setSub] = useState(initial?.subcategory || "");
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(null);
  const [vals, setVals] = useState(() => Object.fromEntries((initial?.data?.extra || []).map((x) => [x.id, x.value])));
  const defaults = useRef({});
  useEffect(() => {
    if (!allP) allP = api.get("/form-config").then((r) => r.data).catch(() => { allP = null; return {}; });
    allP.then(setAll);
  }, []);
  const key = sub ? `${cat}:${sub}` : cat;
  useEffect(() => { setDraft(null); }, [key]);
  const filled = (c) => c && Object.values(c).some((x) => (Array.isArray(x) ? x.length : x && Object.keys(x).length));
  const cfg = draft || { ...EMPTY, ...((filled(all[key]) && all[key]) || all[cat] || {}) };
  const ref = useRef();
  ref.current = { cfg, vals };
  // Attach custom fields to the listing payload on create/update
  useEffect(() => {
    const id = api.interceptors.request.use((c) => {
      const m = (c.method || "").toLowerCase();
      if (/^\/listings(\/[^/]+)?$/.test(c.url || "") && (m === "post" || m === "put") && c.data && typeof c.data === "object") {
        const { cfg: cf, vals: v } = ref.current;
        const extra = (cf.extra || []).filter((x) => !(cf.hidden || []).includes(`extra:${x.id}`)).map((x) => ({ id: x.id, label: x.label, value: String(v[x.id] || "").slice(0, 2000) })).filter((x) => x.value.trim());
        const sv = (c.data.data && c.data.data.services) || [];
        const svc_icons = Object.fromEntries(Object.entries(cf.icons || {}).filter(([k]) => sv.includes(k)));
        c.data = { ...c.data, data: { ...(c.data.data || {}), extra, svc_icons } };
      }
      return c;
    });
    return () => api.interceptors.request.eject(id);
  }, []);
  const change = (fn) => setDraft((d) => fn(JSON.parse(JSON.stringify(d || cfg))));
  const save = async () => {
    try {
      const r = await api.put(`/admin/form-config/${encodeURIComponent(key)}`, cfg);
      setAll((a) => ({ ...a, [key]: r.data }));
      allP = null;
      setDraft(null);
      toast.success("Formulari u ruajt");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const v = useMemo(() => ({
    cfg, edit, change, setSub, vals, setVals, defaults,
    hidden: (t) => (cfg.hidden || []).includes(t),
    hiddenPaid: (k) => (cfg.hidden || []).includes(`paid:${k}`),
    title: (t) => cfg.titles?.[t] || t,
    opts: (n, d) => { defaults.current[n] = d; return cfg.options?.[n]?.length ? cfg.options[n] : d; },
  }), [cfg, edit, vals]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Ctx.Provider value={v}>
      {admin && (
        <div className="sticky top-16 z-30 mx-4 mt-3 flex gap-2" data-testid="formcfg-bar">
          <button data-testid="formcfg-toggle" onClick={() => { if (edit && draft && !window.confirm("Ndryshimet nuk janë ruajtur. T'i heq?")) return; setEdit(!edit); if (edit) setDraft(null); }} className={`flex-1 h-10 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 mb-1 ${edit ? "btn-3d-soft text-slate-700" : "btn-3d-green"}`}>
            <Settings2 className="w-4 h-4" />{edit ? "Mbyll ndryshimin" : `Ndrysho formularin${sub ? ` (${sub})` : " (tërë kategoria)"}`}
          </button>
          {edit && <button data-testid="formcfg-save" onClick={save} className="btn-3d-green h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5 mb-1"><Save className="w-4 h-4" />Ruaj</button>}
        </div>
      )}
      {edit && <div data-testid="formcfg-scope" className="mx-4 mt-2 text-[11px] font-semibold text-amber-800 bg-amber-50 rounded-xl px-3 py-2">{sub ? `Ndryshimet ruhen vetëm për nënkategorinë "${sub}".` : "Ndryshimet ruhen për tërë kategorinë. Zgjidh nënkategorinë në formular për ta ndryshuar veç atë."}</div>}
      {edit && <OptionsEditor />}
      {children}
    </Ctx.Provider>
  );
}

export const useFormCfg = () => useContext(Ctx) || NOOP;

export function useFormSub(value) {
  const fc = useFormCfg();
  useEffect(() => { if (fc.setSub) fc.setSub(value || ""); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
}

function IconPick({ name }) {
  const fc = useFormCfg();
  const [open, setOpen] = useState(false);
  const cur = fc.cfg.icons?.[name];
  const set = (e) => { fc.change((c) => { c.icons = { ...(c.icons || {}) }; if (e) c.icons[name] = e; else delete c.icons[name]; return c; }); setOpen(false); };
  return (
    <span className="relative">
      <button type="button" data-testid={`formcfg-icon-${name}`} onClick={() => setOpen(!open)} className="w-6 h-6 rounded-full bg-slate-100 text-sm flex items-center justify-center" title="Ikona">{cur || <Plus className="w-3 h-3 text-slate-500" />}</button>
      {open && (
        <div className="absolute z-40 top-8 left-0 w-64 bg-white border border-slate-200 rounded-xl shadow-xl p-2 flex flex-wrap gap-1" data-testid={`formcfg-icon-grid-${name}`}>
          {ICON_CHOICES.map((e) => <button key={e} type="button" data-testid={`formcfg-icon-pick-${name}-${e}`} onClick={() => set(e)} className={`w-8 h-8 rounded-lg text-lg ${cur === e ? "bg-emerald-100" : "hover:bg-slate-100"}`}>{e}</button>)}
          <button type="button" onClick={() => { const v = window.prompt("Shkruaj ose ngjit një emoji:", cur || ""); if (v !== null) set(v.trim().slice(0, 8)); }} className="h-8 px-2 rounded-lg bg-slate-100 text-[11px] font-bold">Tjetër</button>
          {cur && <button type="button" onClick={() => set("")} className="h-8 px-2 rounded-lg bg-rose-50 text-rose-600 text-[11px] font-bold">Hiq</button>}
        </div>
      )}
    </span>
  );
}

const lbl = (x) => (typeof x === "string" ? x : x.label);

function OptionsEditor() {
  const fc = useFormCfg();
  const [, force] = useState(0);
  useEffect(() => { const t = setTimeout(() => force(1), 300); return () => clearTimeout(t); }, []);
  const groups = Object.entries(fc.defaults.current);
  const NAMES = { services: "Opsionet / shërbimet", payments: "Metodat e pagesës", benefits: "Përfitimet" };
  if (!groups.length) return null;
  return (
    <div className="mx-4 mt-3 bg-amber-50 border-2 border-dashed border-amber-300 rounded-2xl p-3 space-y-3" data-testid="formcfg-options">
      <div className="text-xs font-bold text-amber-800">Admin: ndrysho opsionet që zgjedhin përdoruesit</div>
      {groups.map(([n, def]) => {
        const list = fc.cfg.options?.[n]?.length ? fc.cfg.options[n] : def;
        const set = (nl) => fc.change((c) => { c.options = { ...(c.options || {}), [n]: nl }; return c; });
        return (
          <div key={n}>
            <div className="text-sm font-bold text-slate-800 mb-1">{NAMES[n] || n}</div>
            <div className="flex flex-wrap gap-1.5">
              {list.map((x, i) => (
                <span key={i} className="flex items-center bg-white border border-slate-200 rounded-full pl-1 pr-1 h-8">
                  {n === "services" && <IconPick name={lbl(x)} />}
                  <input data-testid={`formcfg-opt-${n}-${i}`} value={lbl(x)} onChange={(e) => set(list.map((y, j) => (j === i ? (typeof y === "string" ? e.target.value : { ...y, label: e.target.value }) : y)))} className="text-xs px-2 w-24 outline-none bg-transparent" />
                  <button data-testid={`formcfg-opt-del-${n}-${i}`} onClick={() => set(list.filter((_, j) => j !== i))} className="text-rose-500"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
              <button data-testid={`formcfg-opt-add-${n}`} onClick={() => set([...list, typeof def[0] === "string" ? "I ri" : { key: `x${Date.now()}`, label: "I ri" }])} className="h-8 px-3 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Shto</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Section header: title + admin rename/hide controls
export function SecTitle({ t }) {
  const fc = useFormCfg();
  const pk = useContext(PaidKeyCtx);
  if (!fc.edit) return fc.title(t);
  const hidden = fc.hidden(t);
  const toggle = () => fc.change((c) => {
    const keys = [t, ...(pk ? [`paid:${pk}`] : [])];
    c.hidden = hidden ? (c.hidden || []).filter((h) => !keys.includes(h)) : [...new Set([...(c.hidden || []), ...keys])];
    return c;
  });
  const rename = () => {
    const n = window.prompt("Titulli i ri i pjesës:", fc.title(t));
    if (n !== null) fc.change((c) => { c.titles = { ...(c.titles || {}) }; if (n.trim() && n.trim() !== t) c.titles[t] = n.trim(); else delete c.titles[t]; return c; });
  };
  const locked = ["Informacioni", "Vendndodhja"].includes(t);
  return (
    <span className="flex items-center gap-1.5 normal-case">
      <span className={hidden ? "line-through text-slate-400" : ""}>{fc.title(t)}</span>
      <button type="button" data-testid={`formcfg-rename-${t}`} onClick={rename} className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center"><Pencil className="w-3.5 h-3.5" /></button>
      {!locked && <button type="button" data-testid={`formcfg-hide-${t}`} onClick={toggle} className={`w-7 h-7 rounded-full flex items-center justify-center ${hidden ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-emerald-700"}`}>{hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>}
    </span>
  );
}

// Custom text sections added by admin
export function ExtraFields() {
  const fc = useFormCfg();
  const list = fc.cfg.extra || [];
  if (!list.length && !fc.edit) return null;
  const add = () => { const n = window.prompt("Emri i pjesës së re (p.sh. Garancia, Kushtet):"); if (n && n.trim()) fc.change((c) => { c.extra = [...(c.extra || []), { id: `f${Date.now()}`, label: n.trim() }]; return c; }); };
  return (
    <div data-testid="formcfg-extra">
      {list.map((x) => {
        const hid = fc.hidden(`extra:${x.id}`);
        if (hid && !fc.edit) return null;
        return (
          <section key={x.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mt-3">
            <div className="flex items-center gap-2 mb-2">
              <h2 className={`font-bold text-slate-800 text-sm uppercase tracking-wide flex-1 ${hid ? "line-through text-slate-400" : ""}`}>{x.label}</h2>
              {fc.edit && <>
                <button type="button" data-testid={`formcfg-extra-rename-${x.id}`} onClick={() => { const n = window.prompt("Emri:", x.label); if (n && n.trim()) fc.change((c) => { c.extra = c.extra.map((y) => (y.id === x.id ? { ...y, label: n.trim() } : y)); return c; }); }} className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center"><Pencil className="w-3.5 h-3.5" /></button>
                <button type="button" data-testid={`formcfg-extra-del-${x.id}`} onClick={() => fc.change((c) => { c.extra = c.extra.filter((y) => y.id !== x.id); return c; })} className="w-7 h-7 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
              </>}
            </div>
            <textarea data-testid={`extra-field-${x.id}`} value={fc.vals[x.id] || ""} onChange={(e) => fc.setVals((v) => ({ ...v, [x.id]: e.target.value }))} rows={3} maxLength={2000} placeholder="Shkruaj këtu…" className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none" />
          </section>
        );
      })}
      {fc.edit && <button type="button" data-testid="formcfg-extra-add" onClick={add} className="w-full mt-3 h-12 rounded-2xl border-2 border-dashed border-amber-400 bg-amber-50 text-amber-800 font-bold flex items-center justify-center gap-1.5"><Plus className="w-4 h-4" />Shto pjesë të re</button>}
    </div>
  );
}
